import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Ripple } from "primereact/ripple";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Checkbox, CheckboxChangeEvent } from "primereact/checkbox";
import { FSFeature, FSProject, FeatureOption } from "../../Controllers/Types/FinishingSchedule/FinishingSchedule";

interface FSReportConfigModalProps {
    isOpen: boolean;
    onClose: () => void;
    fsData: FSProject | null;
    defaultSelectedOptions?: FeatureOption[];
    onPreview?: (fsData: FSProject, selectedOptions: FeatureOption[]) => void;
}

const FSReportConfigModal: React.FC<FSReportConfigModalProps> = ({
    isOpen,
    onClose,
    fsData,
    defaultSelectedOptions = [],
    onPreview,
}) => {
    const navigate = useNavigate();

    const [featuresList] = useState<FeatureOption["feature"][]>([
        "Light",
        "Door",
        "Paint",
        "Bathroom",
        "Kitchen",
        "Tiles",
        "Floor Finishing",
        "Others",
    ]);

    const [fsFeatures, setFSFeatures] = useState<FSFeature[]>([]);
    const [selectedFeatureOptions, setSelectedFeatureOptions] = useState<FeatureOption[]>([]);
    const [isAllOptionChecked, setIsAllOptionChecked] = useState(false);

    const accessoryAvailableFeatures = ["Door", "Paint", "Tiles", "Floor Finishing"];

    useEffect(() => {
        if (!fsData) return;
        const availableFeatures = fsData?.feature_list || [];
        setFSFeatures(availableFeatures);

        // restore previous selection if any
        setSelectedFeatureOptions(defaultSelectedOptions || []);
        setIsAllOptionChecked(
            defaultSelectedOptions?.length === availableFeatures?.length
        );
    }, [fsData, defaultSelectedOptions]);

    const handleCloseConfigModal = () => {
        onClose();
    };

    const handlePreviewReport = () => {
        if (!fsData) return;

        if (onPreview) {
            onPreview(fsData, selectedFeatureOptions);
        } else {
            navigate("report", {
                state: {
                    fsData,
                    selectedFeatureOptions,
                },
            });
        }
    };

    // Checkbox handler
    const handleCheckOption = (
        e: CheckboxChangeEvent,
        option:
            | "All"
            | "Light"
            | "Door"
            | "Paint"
            | "Bathroom"
            | "Kitchen"
            | "Tiles"
            | "Floor Finishing"
            | "Others",
        isAccessory?: boolean
    ) => {
        if (!fsFeatures) return;

        if (option === "All") {
            const checked = e.checked ?? false;
            setIsAllOptionChecked(checked);

            if (checked) {
                const allOptions: FeatureOption[] = fsFeatures.map((f) => ({
                    featureId: f.id,
                    feature: f.feature,
                    accessoryView: accessoryAvailableFeatures.includes(f.feature)
                        ? 1
                        : 0,
                }));
                setSelectedFeatureOptions(allOptions);
            } else {
                setSelectedFeatureOptions([]);
            }
            return;
        }

        const featureData = fsFeatures.find((f) => f.feature === option);
        if (!featureData) return;

        setSelectedFeatureOptions((prev) => {
            let updated: FeatureOption[] = [...prev];
            const alreadySelected = updated.find((f) => f.feature === option);

            // Accessory toggle
            if (isAccessory) {
                if (alreadySelected) {
                    updated = updated.map((f) =>
                        f.feature === option
                            ? {
                                ...f,
                                accessoryView: f.accessoryView === 1 ? 0 : 1,
                            }
                            : f
                    );
                }
                return updated;
            }

            // Normal feature checkbox
            if (e.checked) {
                updated.push({
                    featureId: featureData.id,
                    feature: featureData.feature,
                    accessoryView: 0,
                });
            } else {
                updated = updated.filter((f) => f.feature !== option);
            }

            const totalAvailable = fsFeatures.length;
            const totalSelected = updated.length;
            setIsAllOptionChecked(totalSelected === totalAvailable);

            return updated;
        });
    };

    const isFeatureChecked = (feature: string) =>
        selectedFeatureOptions.some((f) => f.feature === feature);

    const isAccessoryChecked = (feature: string) => {
        const option = selectedFeatureOptions.find((f) => f.feature === feature);
        return option?.accessoryView === 1;
    };

    const isFeatureAvailable = (feature: string) =>
        fsFeatures.some((f) => f.feature === feature);

    const modalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">Report view</h5>

            <button
                type="button"
                className="close_modal_btn p-ripple"
                onClick={handleCloseConfigModal}
            >
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    );

    const modalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Close"
                className="custom_btn secondary"
                onClick={handleCloseConfigModal}
            />

            <Button
                label="Preview"
                onClick={handlePreviewReport}
                disabled={selectedFeatureOptions?.length === 0}
                className="custom_btn primary"
            />
        </div>
    );

    return (
        <Dialog
            visible={isOpen}
            header={modalHeader}
            footer={modalFooter}
            headerClassName="custom_modal_header"
            className="custom_modal_dialog modal_dialog_sm"
            onHide={handleCloseConfigModal}
            draggable={false}
            dismissableMask
        >
            <div className="custom_modal_body">
                <div className="p-1 w-100">
                    <div className="row">
                        <div className="col-12">
                            <h6 className="data_view_head mb-2 mb-sm-3">
                                Finishing schedule report
                            </h6>
                        </div>

                        <div className="col-12">
                            <div className="data_check_area mb-0">
                                <div className="data_check_group main">
                                    <label htmlFor="All" className="data_check_label">
                                        All
                                    </label>
                                    <Checkbox
                                        inputId="All"
                                        onChange={(e) => handleCheckOption(e, "All")}
                                        checked={isAllOptionChecked}
                                    />
                                </div>

                                {featuresList.map((feature) => {
                                    const available = isFeatureAvailable(feature);
                                    const showAccessory =
                                        accessoryAvailableFeatures.includes(feature);

                                    return (
                                        <div
                                            key={feature}
                                            className={`data_check_group_area ${!available ? "disabled" : ""
                                                }`}
                                        >
                                            <div className="data_check_group custom">
                                                <label
                                                    htmlFor={feature}
                                                    className="data_check_label"
                                                >
                                                    {feature}
                                                </label>

                                                <Checkbox
                                                    inputId={feature}
                                                    onChange={(e) =>
                                                        handleCheckOption(e, feature)
                                                    }
                                                    checked={isFeatureChecked(feature)}
                                                    disabled={!available}
                                                />
                                            </div>

                                            {showAccessory && (
                                                <div className="data_check_group_sub">
                                                    <label
                                                        htmlFor={`${feature}_accessory`}
                                                        className="data_check_label"
                                                    >
                                                        <i className="bi bi-arrow-return-right me-1"></i>
                                                        Accessory
                                                    </label>

                                                    <Checkbox
                                                        inputId={`${feature}_accessory`}
                                                        onChange={(e) =>
                                                            handleCheckOption(
                                                                e,
                                                                feature,
                                                                true
                                                            )
                                                        }
                                                        checked={isAccessoryChecked(feature)}
                                                        disabled={
                                                            !available ||
                                                            !isFeatureChecked(feature)
                                                        }
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Dialog>
    );
};

export default FSReportConfigModal;
